import React, { useState, useEffect } from 'react';
import { labelStyle, formatShortDate } from './KanbanCard';

function formatFullDate(dateString) {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export default function KanbanArchive({ isEditable }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchItems = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      setError('');
      const response = await fetch('/api/admin?action=kanban&view=archive');
      const data = await response.json();
      if (data.success) {
        setItems(data.items || []);
      } else {
        setError(data.error || 'Failed to load archive');
      }
    } catch (err) {
      console.error('Error fetching archive:', err);
      setError('Failed to load archive');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(true);
  }, []);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
  });

  const handleRestore = async (itemId) => {
    if (!isEditable) return;
    try {
      const response = await fetch('/api/admin?action=kanban', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: itemId, unarchive: true })
      });
      const data = await response.json();
      if (data.success) {
        setItems(prev => prev.filter(i => i.id !== itemId));
      } else {
        throw new Error(data.error || 'Failed to restore item');
      }
    } catch (err) {
      console.error('Error restoring item:', err);
      alert('Failed to restore card. Please try again.');
    }
  };

  const handleDelete = async (itemId) => {
    if (!isEditable) return;
    if (!window.confirm('Permanently delete this archived card?')) return;
    try {
      const response = await fetch('/api/admin?action=kanban', {
        method: 'DELETE',
        headers: authHeaders(),
        body: JSON.stringify({ id: itemId })
      });
      const data = await response.json();
      if (data.success) {
        setItems(prev => prev.filter(i => i.id !== itemId));
      } else {
        throw new Error(data.error || 'Failed to delete item');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Failed to delete card. Please try again.');
    }
  };

  const categories = [...new Set(
    items.map(i => (i.category || '').trim()).filter(Boolean)
  )].sort();

  const filteredItems = items.filter(item => {
    if (activeCategory && (item.category || '').trim() !== activeCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const title = (item.title || '').toLowerCase();
      const desc = (item.description || '').toLowerCase();
      if (!title.includes(q) && !desc.includes(q)) return false;
    }
    return true;
  });

  const chipClass = (active) =>
    `px-2.5 py-1 rounded-full text-xs transition-colors touch-manipulation whitespace-nowrap ${
      active
        ? 'bg-neutral-200 text-neutral-900 font-medium'
        : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800'
    }`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-neutral-700 border-t-neutral-200" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <p className="text-sm text-neutral-400 mb-3">{error}</p>
        <button
          onClick={() => fetchItems(true)}
          className="px-3 py-1.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-sm transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Toolbar */}
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
        <div className="flex items-center gap-1 overflow-x-auto smooth-scroll min-w-0 pb-0.5">
          <button onClick={() => setActiveCategory(null)} className={chipClass(!activeCategory)}>
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={chipClass(activeCategory === cat)}
            >
              {cat}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search archive…"
          className="sm:ml-auto w-full sm:w-48 px-2.5 py-1 rounded-lg bg-neutral-800 border border-neutral-700/60 text-xs text-neutral-200 placeholder-neutral-500 outline-none focus:border-neutral-500 transition-colors"
        />
      </div>

      {/* Info banner */}
      <p className="shrink-0 text-xs text-neutral-500 mb-3">
        Cards in Done for more than 2 weeks are moved here automatically.
        {isEditable && ' You can restore them to the board or delete permanently.'}
      </p>

      {/* Archive list */}
      {filteredItems.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-neutral-500">
            {items.length === 0 ? 'No archived cards yet.' : 'No cards match your filter.'}
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pb-3">
          {filteredItems.map(item => (
            <div
              key={item.id}
              className="rounded-lg bg-neutral-800/60 border border-neutral-700/40 px-3 py-2.5"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {item.category && (
                    <span className={`inline-block px-1.5 py-px rounded text-[10px] font-medium mb-1 ${labelStyle(item.category)}`}>
                      {item.category}
                    </span>
                  )}
                  <div className="text-sm text-neutral-100 leading-snug break-words">
                    {item.title}
                  </div>
                  {item.description && (
                    <p className="mt-1 text-xs text-neutral-400 line-clamp-2 break-words">
                      {item.description}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-neutral-500">
                    {item.finishedAt && (
                      <span>Finished {formatShortDate(item.finishedAt)}</span>
                    )}
                    {item.archivedAt && (
                      <span>Archived {formatFullDate(item.archivedAt)}</span>
                    )}
                  </div>
                </div>
                {isEditable && (
                  <div className="shrink-0 flex items-center gap-1.5">
                    <button
                      onClick={() => handleRestore(item.id)}
                      className="px-2 py-1 rounded text-[11px] font-medium text-sky-300/90 bg-sky-500/10 hover:bg-sky-500/20 hover:text-sky-200 transition-colors touch-manipulation"
                      title="Restore to Done column"
                    >
                      Restore
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="px-2 py-1 rounded text-[11px] font-medium text-rose-400/80 bg-rose-500/10 hover:bg-rose-500/20 hover:text-rose-300 transition-colors touch-manipulation"
                      title="Delete permanently"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
